import { useCallback, useMemo, useState } from 'react'

import { useDebounceCallback } from 'usehooks-ts'

import { Event, EventListOrderByEnum } from '@tribeplatform/gql-client/types'
import { useSearchEventForEmbed } from '@tribeplatform/react-sdk/hooks'
import { simplifyPaginatedResult } from '@tribeplatform/react-sdk/utils'
import { FormControlSearchableSelect } from '@tribeplatform/react-ui-kit/FormControl/FormControlSearchableSelect'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'

import { T, useI18n } from '../../i18n/index.js'
import { getEventFormattedDate } from '../utils.js'

interface Props {
  value?: Event
  onChange?: (event: Event) => void
}

export const EventPicker = ({ value, onChange }: Props) => {
  const [query, setQuery] = useState('')
  const { $t } = useI18n()

  const { data } = useSearchEventForEmbed({
    variables: {
      limit: 20,
      query,
      orderBy: EventListOrderByEnum.startTime,
      reverse: true,
    },
    useInfiniteQueryOptions: {
      enabled: !!query,
    },
  })

  const debouncedGetPlacePredictions = useDebounceCallback(setQuery, 500)
  const events = useMemo(
    () => simplifyPaginatedResult<Event>(data)?.nodes || [],
    [data],
  )

  const options = useMemo(() => {
    const items = events.map(event => ({
      value: event.id,
      key: event.title,
      text: <SuggestedEventItem event={event} />,
    }))

    if (!items.length && value) {
      items.push({
        value: value.id,
        key: value.title,
        text: <SuggestedEventItem event={value} />,
      })
    }

    return items
  }, [events, value])

  const onSelect = useCallback(
    (id: Event['id']) => {
      const event = events.find(event => event.id === id)
      if (event) {
        onChange?.(event)
      }
    },
    [events, onChange],
  )

  return (
    <FormControlSearchableSelect
      name="event"
      label={<T defaultMessage="Event" id="Generics.Event" />}
      options={options}
      onInputChange={debouncedGetPlacePredictions}
      onChange={onSelect}
      value={value?.id}
      placeholder={$t({
        defaultMessage: 'Search for an event',
        id: 'Post.Composer.Event.Embed.Search',
      })}
      hideEmptyOptions
      autoFocus
    />
  )
}

const SuggestedEventItem = ({ event }: { event: Event }) => {
  const { date, time } = getEventFormattedDate({
    startTime: event.startTime,
    endTime: event.endTime,
  })

  return (
    <div className="flex gap-x-2 truncate">
      <SvgIcon name="calendar" className="size-6 text-content-muted" />
      <div className="truncate pe-6 flex flex-col gap-y-1">
        {event.title}
        {date && time && (
          <div className="text-sm text-content-muted">
            {date} {time}
          </div>
        )}
      </div>
    </div>
  )
}
